import './App.css';
import { useEffect, useMemo, useRef, useState } from 'react';

import avatar from './Assets/1.jpeg';

const LINKEDIN_URL = 'https://www.linkedin.com/in/kanishka-kumarasiri/';
const JOURNAL_URL = 'https://journals.sjp.ac.lk/index.php/vjhss/article/view/7853';

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      setReduced(false);
      return undefined;
    }

    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(Boolean(media.matches));
    onChange();

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }

    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  return reduced;
}

function ThemeToggle({ theme, onToggle }) {
  return (
    <button
      type="button"
      className="iconButton"
      onClick={onToggle}
      aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
      title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
    >
      <span className="srOnly">
        {theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
      </span>
      <span className="iconButtonIcon" aria-hidden="true">
        {theme === 'dark' ? (
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M12 17.5a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11Z"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <path
              d="M12 2.5v2.1M12 19.4v2.1M21.5 12h-2.1M4.6 12H2.5M18.9 5.1l-1.5 1.5M6.6 17.4l-1.5 1.5M18.9 18.9l-1.5-1.5M6.6 6.6 5.1 5.1"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M21 14.2A7.2 7.2 0 0 1 9.8 3a6 6 0 1 0 11.2 11.2Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
    </button>
  );
}

function Tag({ children }) {
  return <span className="tag">{children}</span>;
}

function Section({ id, title, subtitle, children }) {
  return (
    <section id={id} className="section" data-reveal="true">
      <div className="sectionHead">
        <h2 className="sectionTitle">{title}</h2>
        {subtitle ? <p className="sectionSubtitle">{subtitle}</p> : null}
      </div>
      <div className="sectionBody">{children}</div>
    </section>
  );
}

function App() {
  const reducedMotion = usePrefersReducedMotion();
  const [theme, setTheme] = useState('dark');
  const [navOpen, setNavOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const headerRef = useRef(null);

  const content = useMemo(
    () => ({
      name: 'Kanishka Kumarasiri',
      role: 'Data Engineer • Software Developer • Mobile Developer',
      aboutShort:
        'Passionate about engineering and experienced in software, data engineering, and mobile development. Proficient in problem-solving and committed to delivering innovative, data-driven solutions.',
      aboutLong:
        "Proactive Data Engineer with hands-on experience in enterprise-grade data engineering and analytics projects across banking, healthcare, manufacturing, and aviation. Skilled in .NET, databases, and scripting, with a proven ability to design, develop, and maintain scalable solutions. Recognized for strong problem-solving, communication, and teamwork in distributed global teams — and always eager for exciting opportunities that challenge and enable continuous growth.",
      expertise: [
        {
          title: 'Programming & Frameworks',
          items: ['.NET', 'Python', 'Node.js', 'Express', 'PL/SQL', 'React', 'Electron'],
        },
        {
          title: 'Databases',
          items: ['Oracle', 'SQL', 'SQLite', 'Azure Synapse Analytics', 'Oracle Data Integrator'],
        },
        {
          title: 'Tools & Platforms',
          items: ['Docker', 'SSIS', 'Apache Superset', 'Apache Airflow', 'Git', 'Azure Fabric'],
        },
        {
          title: 'Concepts',
          items: ['Data Pipelines', 'Data Warehousing', 'Analytics', 'Orchestration', 'Scripting'],
        },
        {
          title: 'Soft Skills',
          items: ['Problem Solving', 'Fast Learning', 'Technology Adaptation', 'Teamwork'],
        },
      ],
      experience: [
        {
          title: 'Engineer – Data Engineering & Analytics',
          org: 'Millennium IT ESP',
          time: 'Mar 2025 – Present',
          highlights: [
            'UL Datalake - Contributed to SriLankan Airlines enterprise data lake initiatives, supporting scalable ingestion, transformation, and analytics use cases.',
            'Delivered enterprise data lake projects for HCB (Hemas Consumer Brand), Pharma, Hospitals, Atlas.',
            'Migrated Oracle reports to OAC for Manulife Singapore improving reporting efficiency.',
            'Designed dashboards and managed data warehouse for People’s Bank using Apache Superset & Docker.',
            'Supported Royal Ceramics DWH ensuring performance and reliability.',
            'Contributed to Aswesuma Government Project, implementing data visualization and analytics.',
          ],
        },
        {
          title: 'Associate Engineer – Data Engineering & Analytics',
          org: 'Millennium IT ESP',
          time: 'Dec 2022 – Mar 2025',
          highlights: [
            'Built and maintained Azure Synapse pipelines for Ceylon Biscuits Limited (CBL).',
            'Migrated Azure Synapse to Azure Fabric, ensuring smooth transition and data consistency.',
            'Developed data lake solutions for Hemas Holdings PLC (HCB).',
            'Designed audit data pipelines for SriLankan Airlines.',
            'Implemented data warehousing & visualization for People’s Bank Card Center & Savings.',
            'Designed wireframes for MAS Holdings data capture solutions.',
          ],
        },
      ],
      education: [
        {
          title: 'MSc in Computer Science (Reading)',
          org: 'University of Moratuwa',
          time: 'Reading',
        },
        {
          title: 'BSc (Hons) in Information Technology — Data Science',
          org: 'Sri Lanka Institute of Information Technology (SLIIT)',
          time: '2021 – 2024',
        },
      ],
      certifications: [
        'Microsoft Certified: Fabric Data Engineer Associate (DP700) — 2025',
        'Oracle AI Vector Search Certified Professional — 2025',
        'Meta Front-End Developer Specialization — 2024',
        'Microsoft Certified: Fabric Analytics Engineer Associate (DP600) — 2024',
        'IBM — DataOps Methodology — 2024',
        'Red Hat — Fundamentals of Red Hat Enterprise Linux — 2024',
        'Oracle Cloud Data Management Foundations Associate — 2023',
        'Oracle Cloud Infrastructure Foundations Associate — 2023',
        'DeepLearning.AI — Supervised Machine Learning (Regression & Classification)',
      ],
      projects: [
        {
          title: 'SwaraMala',
          desc: 'Desktop app in Electron & React for Hindustani Ragadhari music notation; published research.',
          link: JOURNAL_URL,
          tags: ['Electron', 'React', 'Research'],
        },
        {
          title: 'XLAB',
          desc: 'Medical Laboratory Management System in Python & SQLite to manage billing, reporting, and operations.',
          tags: ['Python', 'SQLite', 'Desktop'],
        },
      ],
      achievements: [
        'MIT Bravo Recognition Award — 2024',
        'Finalist — SLIIT Extreme Hackathon (Top 10) — 2021',
        'Participant — Apan56 Datathon, Asia Pacific Advanced Network — 2023',
        'Awarded Oriental Music Visharada (Violin) — Qualified for Sangeeth Nipun (Bhatkhande Sangeet Vidyapeeth, India)',
      ],
      volunteering: ['Project Chairperson, Project “Doratuwa” — LEO Club of SLIIT (2023)'],
      hashtags: [
        '#DataScience',
        '#SoftwareDevelopment',
        '#MobileDevelopment',
        '#ProblemSolving',
        '#TechEnthusiast',
      ],
    }),
    []
  );

  useEffect(() => {
    const stored = window.localStorage.getItem('portfolio-theme');
    const preferredDark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
    const initial = stored === 'light' || stored === 'dark' ? stored : preferredDark ? 'dark' : 'light';
    setTheme(initial);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem('portfolio-theme', theme);

    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) metaTheme.setAttribute('content', theme === 'dark' ? '#0b1220' : '#f8fafc');
  }, [theme]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 520);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      document.querySelectorAll('[data-reveal="true"]').forEach((node) => {
        node.classList.add('is-visible');
      });
      return undefined;
    }

    if (typeof IntersectionObserver !== 'function') {
      document.querySelectorAll('[data-reveal="true"]').forEach((node) => {
        node.classList.add('is-visible');
      });
      return undefined;
    }

    const elements = Array.from(document.querySelectorAll('[data-reveal="true"]'));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.14 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [reducedMotion]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  const onToggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  const onNav = () => setNavOpen(false);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });

  return (
    <div className="page">
      <a className="skipLink" href="#content">
        Skip to content
      </a>

      <header className="topbar" ref={headerRef}>
        <div className="topbarInner">
          <a className="brand" href="#home" onClick={onNav}>
            <span className="brandDot" aria-hidden="true" />
            <span className="brandText">{content.name}</span>
          </a>

          <nav className="navDesktop" aria-label="Primary">
            <a className="navLink" href="#about">
              About
            </a>
            <a className="navLink" href="#expertise">
              Expertise
            </a>
            <a className="navLink" href="#experience">
              Experience
            </a>
            <a className="navLink" href="#projects">
              Projects
            </a>
            <a className="navLink" href="#credentials">
              Credentials
            </a>
            <a className="navLink" href="#contact">
              Contact
            </a>
          </nav>

          <div className="topbarActions">
            <ThemeToggle theme={theme} onToggle={onToggleTheme} />
            <button
              type="button"
              className="iconButton hamburger"
              aria-label={navOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={navOpen}
              onClick={() => setNavOpen((v) => !v)}
            >
              <span className="srOnly">{navOpen ? 'Close menu' : 'Open menu'}</span>
              <span className="hamburgerIcon" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>

        <div className={`navMobile ${navOpen ? 'is-open' : ''}`} aria-hidden={!navOpen}>
          <div className="navMobilePanel" role="dialog" aria-label="Menu">
            <a className="navMobileLink" href="#about" onClick={onNav}>
              About
            </a>
            <a className="navMobileLink" href="#expertise" onClick={onNav}>
              Expertise
            </a>
            <a className="navMobileLink" href="#experience" onClick={onNav}>
              Experience
            </a>
            <a className="navMobileLink" href="#projects" onClick={onNav}>
              Projects
            </a>
            <a className="navMobileLink" href="#credentials" onClick={onNav}>
              Credentials
            </a>
            <a className="navMobileLink" href="#contact" onClick={onNav}>
              Contact
            </a>
            <a
              className="navMobileCTA"
              href={LINKEDIN_URL}
              target="_blank"
              rel="noreferrer"
              onClick={onNav}
            >
              Connect on LinkedIn
            </a>
          </div>
          <button type="button" className="navBackdrop" aria-label="Close menu" onClick={onNav} />
        </div>
      </header>

      <main id="content" className="content">
        <section id="home" className="hero">
          <div className="heroBg" aria-hidden="true">
            <div className="blob blobA" />
            <div className="blob blobB" />
            <div className="gridFade" />
          </div>

          <div className="heroInner">
            <div className="heroCopy" data-reveal="true">
              <p className="heroKicker">Open to exciting opportunities</p>
              <h1 className="heroTitle">
                Building data platforms and software that people trust.
              </h1>
              <p className="heroSubtitle">{content.role}</p>
              <p className="heroAbout">{content.aboutShort}</p>
              <div className="heroActions">
                <a className="button primary" href={LINKEDIN_URL} target="_blank" rel="noreferrer">
                  Connect on LinkedIn
                </a>
                <a className="button ghost" href="#projects">
                  View projects
                </a>
              </div>
              <div className="heroTags" aria-label="Interests">
                {content.hashtags.map((t) => (
                  <Tag key={t}>{t}</Tag>
                ))}
              </div>
            </div>

            <div className="heroCard" data-reveal="true">
              <div className="avatarWrap">
                <img className="avatar" src={avatar} alt={`${content.name} portrait`} />
              </div>
              <div className="heroCardBody">
                <div className="heroName">{content.name}</div>
                <div className="heroMeta">Sri Lanka • Global teams • Enterprise data</div>
                <div className="heroQuick">
                  <div className="quickItem">
                    <div className="quickLabel">Focus</div>
                    <div className="quickValue">Data engineering & analytics</div>
                  </div>
                  <div className="quickItem">
                    <div className="quickLabel">Stack</div>
                    <div className="quickValue">.NET • Python • Azure • Oracle</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Section
          id="about"
          title="About"
          subtitle="A short story about what I do and how I work."
        >
          <div className="card">
            <p className="lead">{content.aboutLong}</p>
            <div className="divider" />
            <div className="twoCol">
              <div className="miniCard">
                <h3 className="miniTitle">What you can expect</h3>
                <ul className="list">
                  <li>Clean, maintainable solutions with clear ownership.</li>
                  <li>Strong collaboration, communication, and documentation.</li>
                  <li>Reliability-first mindset for production data systems.</li>
                </ul>
              </div>
              <div className="miniCard">
                <h3 className="miniTitle">What I enjoy</h3>
                <ul className="list">
                  <li>Designing data pipelines and scalable analytics.</li>
                  <li>Shipping software that improves operations.</li>
                  <li>Learning new tools and turning them into value.</li>
                </ul>
              </div>
            </div>
          </div>
        </Section>

        <Section
          id="expertise"
          title="Area of Expertise"
          subtitle="A practical toolkit across engineering, data, and delivery."
        >
          <div className="grid">
            {content.expertise.map((group) => (
              <div className="tile" key={group.title}>
                <h3 className="tileTitle">{group.title}</h3>
                <div className="tileTags">
                  {group.items.map((item) => (
                    <Tag key={item}>{item}</Tag>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section id="experience" title="Professional Experience" subtitle="Roles and impact.">
          <div className="timeline">
            {content.experience.map((role) => (
              <article className="timelineItem" key={`${role.title}-${role.time}`}>
                <div className="timelineDot" aria-hidden="true" />
                <div className="timelineCard">
                  <div className="timelineTop">
                    <div>
                      <h3 className="timelineTitle">{role.title}</h3>
                      <div className="timelineOrg">{role.org}</div>
                    </div>
                    <div className="timelineTime">{role.time}</div>
                  </div>
                  <ul className="list compact">
                    {role.highlights.map((h) => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </Section>

        <Section id="projects" title="Personal Projects" subtitle="Selected work and research.">
          <div className="grid projects">
            {content.projects.map((p) => (
              <article className="tile projectTile" key={p.title}>
                <div className="projectTop">
                  <h3 className="tileTitle">{p.title}</h3>
                  {p.link ? (
                    <a className="link" href={p.link} target="_blank" rel="noreferrer">
                      Publication
                    </a>
                  ) : null}
                </div>
                <p className="muted">{p.desc}</p>
                <div className="tileTags">
                  {p.tags.map((t) => (
                    <Tag key={t}>{t}</Tag>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </Section>

        <Section
          id="credentials"
          title="Education & Certifications"
          subtitle="Learning never stops."
        >
          <div className="twoCol">
            <div className="card">
              <h3 className="miniTitle">Education</h3>
              <div className="stack">
                {content.education.map((e) => (
                  <div className="row" key={e.title}>
                    <div className="rowMain">
                      <div className="rowTitle">{e.title}</div>
                      <div className="rowSub">{e.org}</div>
                    </div>
                    <div className="rowMeta">{e.time}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card">
              <h3 className="miniTitle">Certifications</h3>
              <ul className="list compact">
                {content.certifications.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="twoCol">
            <div className="card">
              <h3 className="miniTitle">Achievements</h3>
              <ul className="list compact">
                {content.achievements.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </div>
            <div className="card">
              <h3 className="miniTitle">Volunteer Leadership</h3>
              <ul className="list compact">
                {content.volunteering.map((v) => (
                  <li key={v}>{v}</li>
                ))}
              </ul>
            </div>
          </div>
        </Section>

        <Section id="contact" title="Contact" subtitle="Let’s connect and explore collaborations.">
          <div className="ctaCard">
            <div>
              <h3 className="ctaTitle">Want to talk data, software, or product ideas?</h3>
              <p className="muted">
                I’m always happy to discuss data platforms, analytics, and modern app development.
              </p>
            </div>
            <div className="ctaActions">
              <a className="button primary" href={LINKEDIN_URL} target="_blank" rel="noreferrer">
                Message on LinkedIn
              </a>
              <a className="button ghost" href="#home">
                Back to top
              </a>
            </div>
          </div>
        </Section>

        <footer className="footer">
          <div className="footerInner">
            <div className="footerLeft">© {new Date().getFullYear()} {content.name}</div>
            <div className="footerRight">
              <a className="link" href={LINKEDIN_URL} target="_blank" rel="noreferrer">
                LinkedIn
              </a>
            </div>
          </div>
        </footer>
      </main>

      <button
        type="button"
        className={`toTop ${showTop ? 'is-showing' : ''}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <span aria-hidden="true">↑</span>
      </button>
    </div>
  );
}

export default App;
